/**
 * PhoneInput — international phone input with country selector.
 *
 * Splits the value into two fields the backend stores separately:
 *   phone_country_code  — "+57", "+52", "+1" (E.164 with leading +)
 *   phone               — bare digits, no country code, no spaces
 *
 * Why we keep them separate: WhatsApp Business API and most SMS gateways
 * require E.164 (digits only), and we render formatted in the UI. With
 * the parts pre-split we can hand off to the messaging stack without
 * regex parsing.
 *
 * Country list focuses on LATAM (where Taxor operates) + ES + US/CA.
 * The dropdown supports keyboard search.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown, Search } from 'lucide-react'

export interface CountryEntry {
  iso: string         // ISO 3166-1 alpha-2 (CO, MX, US…)
  code: string        // Dial code with leading + ("+57")
  name: string        // Localized display name
  flag: string        // Emoji flag (rendered by the OS)
}

// Order: CO first (Taxor's home market), then alphabetical LATAM, then ES, then NA.
export const COUNTRIES: CountryEntry[] = [
  { iso: 'CO', code: '+57', name: 'Colombia', flag: '🇨🇴' },
  { iso: 'AR', code: '+54', name: 'Argentina', flag: '🇦🇷' },
  { iso: 'BO', code: '+591', name: 'Bolivia', flag: '🇧🇴' },
  { iso: 'BR', code: '+55', name: 'Brasil', flag: '🇧🇷' },
  { iso: 'CL', code: '+56', name: 'Chile', flag: '🇨🇱' },
  { iso: 'CR', code: '+506', name: 'Costa Rica', flag: '🇨🇷' },
  { iso: 'CU', code: '+53', name: 'Cuba', flag: '🇨🇺' },
  { iso: 'EC', code: '+593', name: 'Ecuador', flag: '🇪🇨' },
  { iso: 'SV', code: '+503', name: 'El Salvador', flag: '🇸🇻' },
  { iso: 'GT', code: '+502', name: 'Guatemala', flag: '🇬🇹' },
  { iso: 'HN', code: '+504', name: 'Honduras', flag: '🇭🇳' },
  { iso: 'MX', code: '+52', name: 'México', flag: '🇲🇽' },
  { iso: 'NI', code: '+505', name: 'Nicaragua', flag: '🇳🇮' },
  { iso: 'PA', code: '+507', name: 'Panamá', flag: '🇵🇦' },
  { iso: 'PY', code: '+595', name: 'Paraguay', flag: '🇵🇾' },
  { iso: 'PE', code: '+51', name: 'Perú', flag: '🇵🇪' },
  { iso: 'PR', code: '+1', name: 'Puerto Rico', flag: '🇵🇷' },
  { iso: 'DO', code: '+1', name: 'República Dominicana', flag: '🇩🇴' },
  { iso: 'UY', code: '+598', name: 'Uruguay', flag: '🇺🇾' },
  { iso: 'VE', code: '+58', name: 'Venezuela', flag: '🇻🇪' },
  { iso: 'ES', code: '+34', name: 'España', flag: '🇪🇸' },
  { iso: 'US', code: '+1', name: 'Estados Unidos', flag: '🇺🇸' },
  { iso: 'CA', code: '+1', name: 'Canadá', flag: '🇨🇦' },
]

const DEFAULT_ISO = 'CO'

function findByCode(code: string | null | undefined): CountryEntry | undefined {
  if (!code) return undefined
  return COUNTRIES.find((c) => c.code === code)
}

function findByIso(iso: string): CountryEntry {
  return COUNTRIES.find((c) => c.iso === iso) ?? COUNTRIES[0]
}

export interface PhoneInputProps {
  countryCode: string | null
  phone: string | null
  onChange: (next: { countryCode: string; phone: string }) => void
  disabled?: boolean
  required?: boolean
  autoFocus?: boolean
  id?: string
}

export function PhoneInput({
  countryCode,
  phone,
  onChange,
  disabled,
  required,
  autoFocus,
  id,
}: PhoneInputProps) {
  // The selected country is whichever of our list matches the saved
  // country_code; if multiple share a dial code (US/CA/PR/DO all "+1"),
  // we fall back to the first match — fine since the underlying value
  // is identical from the server's POV.
  const initialIso = useMemo(() => findByCode(countryCode)?.iso ?? DEFAULT_ISO, [countryCode])
  const [iso, setIso] = useState(initialIso)
  const country = findByIso(iso)
  const digits = (phone ?? '').replace(/\D/g, '')

  const update = (nextIso: string, nextDigits: string) => {
    setIso(nextIso)
    const nextCountry = findByIso(nextIso)
    onChange({ countryCode: nextCountry.code, phone: nextDigits })
  }

  return (
    <div className="flex gap-2">
      <CountryDropdown
        value={iso}
        onChange={(nextIso) => update(nextIso, digits)}
        disabled={disabled}
      />
      <input
        id={id}
        type="tel"
        inputMode="numeric"
        autoComplete="tel-national"
        autoFocus={autoFocus}
        required={required}
        disabled={disabled}
        value={digits}
        onChange={(e) => update(iso, e.target.value.replace(/\D/g, ''))}
        placeholder={country.iso === 'CO' ? '3001234567' : 'Sólo números'}
        maxLength={15}
        className="w-full rounded-md border border-border bg-bg-app px-3 py-2 text-sm text-fg-primary outline-none transition-colors placeholder:text-fg-muted focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 disabled:cursor-not-allowed disabled:opacity-60"
      />
    </div>
  )
}

// ─── Country dropdown ──────────────────────────────────────────────

function CountryDropdown({
  value,
  onChange,
  disabled,
}: {
  value: string
  onChange: (iso: string) => void
  disabled?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [filter, setFilter] = useState('')
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const country = findByIso(value)

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return
    function onDocClick(e: MouseEvent) {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKey)
    inputRef.current?.focus()
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const filtered = useMemo(() => {
    if (!filter) return COUNTRIES
    const f = filter.toLowerCase()
    return COUNTRIES.filter(
      (c) =>
        c.name.toLowerCase().includes(f) ||
        c.iso.toLowerCase().includes(f) ||
        c.code.includes(f),
    )
  }, [filter])

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex h-full items-center gap-1.5 rounded-md border border-border bg-bg-app px-2 py-2 text-sm text-fg-primary outline-none transition-colors hover:bg-bg-elevated focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 disabled:cursor-not-allowed disabled:opacity-60"
      >
        <span aria-hidden className="text-base leading-none">{country.flag}</span>
        <span className="font-mono text-xs text-fg-secondary">{country.code}</span>
        <ChevronDown className={`h-3.5 w-3.5 text-fg-muted transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute left-0 top-full z-50 mt-1 max-h-72 w-72 overflow-hidden rounded-lg border border-border bg-bg-surface shadow-xl"
        >
          <div className="flex items-center gap-2 border-b border-border bg-bg-app/40 px-3 py-2">
            <Search className="h-3.5 w-3.5 text-fg-muted" />
            <input
              ref={inputRef}
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              onKeyDown={(e) => {
                // Prevent the parent form from submitting when the user
                // hits Enter while typing in the search box. Instead,
                // pick the first match (if any).
                if (e.key === 'Enter') {
                  e.preventDefault()
                  e.stopPropagation()
                  if (filtered[0]) {
                    onChange(filtered[0].iso)
                    setOpen(false)
                    setFilter('')
                  }
                }
              }}
              placeholder="Buscar país…"
              className="w-full bg-transparent text-sm text-fg-primary outline-none placeholder:text-fg-muted"
            />
          </div>
          <ul className="max-h-56 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-sm text-fg-muted">Sin resultados</li>
            ) : (
              filtered.map((c) => (
                <li key={c.iso}>
                  <button
                    type="button"
                    onClick={() => {
                      onChange(c.iso)
                      setOpen(false)
                      setFilter('')
                    }}
                    aria-selected={c.iso === value}
                    className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-bg-elevated ${
                      c.iso === value ? 'bg-bg-elevated font-medium' : ''
                    }`}
                  >
                    <span aria-hidden className="text-base leading-none">{c.flag}</span>
                    <span className="flex-1 truncate text-fg-primary">{c.name}</span>
                    <span className="font-mono text-xs text-fg-muted">{c.code}</span>
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  )
}
